// Public blog index — /blog (pt), /en/blog, /es/blog. Lists published
// posts for the current locale as cards. Dynamic (DB-backed), so it's
// NOT pre-rendered by SSG; the FastAPI catchall serves the SPA shell and
// React fetches the list. Per-post SEO meta is injected server-side.

import { Link } from "react-router-dom";
import { Head } from "vite-react-ssg";

import { Section, SectionHeading } from "@/components/marketing/Section";
import { SiteFooter } from "@/components/marketing/SiteFooter";
import { SiteNav } from "@/components/marketing/SiteNav";
import { useBlogPosts } from "@/hooks/useBlog";
import { LOCALE_HREFLANG, LOCALES, localePath, localeUrl } from "@/i18n/config";
import { useDict, useLocale } from "@/i18n/LocaleProvider";

function formatDate(iso: string | null | undefined, locale: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(LOCALE_HREFLANG[locale as "pt" | "en" | "es"], {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function BlogIndexPage() {
  const locale = useLocale();
  const t = useDict().blog;
  const { loading, posts } = useBlogPosts(locale);
  const base = localePath(locale, "blog");

  return (
    <div className="bg-bg min-h-screen text-content">
      <Head>
        <html lang={LOCALE_HREFLANG[locale]} />
        <title>{t.meta.title}</title>
        <meta name="description" content={t.meta.description} />
        <link rel="canonical" href={localeUrl(locale, "blog")} />
        <meta property="og:url" content={localeUrl(locale, "blog")} />
        <meta property="og:title" content={t.meta.title} />
        <meta property="og:description" content={t.meta.description} />
        {LOCALES.map((loc) => (
          <link
            key={loc}
            rel="alternate"
            hrefLang={LOCALE_HREFLANG[loc]}
            href={localeUrl(loc, "blog")}
          />
        ))}
        <link rel="alternate" hrefLang="x-default" href={localeUrl("pt", "blog")} />
      </Head>

      <SiteNav page="blog" />

      <Section width="1000px">
        <SectionHeading eyebrow={t.eyebrow} title={t.title} lead={t.subtitle} />

        {loading ? (
          <div className="text-center font-mono text-[12px] text-dim py-12">
            {t.loading}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center font-mono text-[12px] text-dim py-12">
            {t.empty}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {posts.map((p) => (
              <Link
                key={p.id}
                to={`${base}/${p.slug}`}
                className="no-underline flex flex-col rounded overflow-hidden group"
                style={{
                  background: "rgb(var(--color-bg2))",
                  border: "1px solid rgb(var(--color-stroke))",
                }}
              >
                {p.cover_image_url && (
                  <img
                    src={p.cover_image_url}
                    alt=""
                    className="w-full h-[160px] object-cover"
                    loading="lazy"
                  />
                )}
                <div className="p-4 flex flex-col flex-1">
                  <div className="font-mono text-[10px] text-dim mb-2">
                    {formatDate(p.published_at, locale)}
                  </div>
                  <h3 className="font-display text-[17px] font-bold text-content leading-[1.3] mb-2 group-hover:text-accent transition-colors">
                    {p.title}
                  </h3>
                  {p.excerpt && (
                    <p className="font-sans text-[13px] text-muted leading-[1.6] flex-1">
                      {p.excerpt}
                    </p>
                  )}
                  <span className="font-mono text-[11px] text-accent mt-3">
                    {t.readMore}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Section>

      <SiteFooter />
    </div>
  );
}
